import React, { useState } from 'react'
import { Link } from 'react-router'
import {
  Panel,
  Text,
  Form,
  RadioGroup,
  Radio,
  ButtonToolbar,
  Button,
  Message,
} from 'rsuite'

import FormField from 'shared/FormField/FormField'

import * as styles from './ForgotPassword.m.css'

enum ForgotPasswordStep {
  WhatsNeeded,
  ForgotUsername,
  ForgotPassword,
  Finished,
}

type FormValue = {
  remembersUsername: string
  username: string
  email: string
}

const RATE_LIMIT_ERROR_MESSAGE = 'Too many attempts. Try again in 15 minutes.'

const ForgotPassword: React.FC = (): React.ReactElement => {
  const [forgotPasswordStep, setForgotPasswordStep] =
    useState<ForgotPasswordStep>(ForgotPasswordStep.WhatsNeeded)
  const [formValue, setFormValue] = useState<FormValue>({
    remembersUsername: 'yes',
    username: '',
    email: '',
  })
  const [rateLimitError, setRateLimitError] = useState<boolean>(false)

  const handleRememberUsernameFormSubmit = (
    value: Record<string, unknown> | null,
  ) => {
    const remembersUsername =
      (value?.remembersUsername as string | undefined) ??
      formValue.remembersUsername

    setFormValue((prev) => ({
      ...prev,
      remembersUsername,
    }))

    if (remembersUsername === 'yes') {
      setForgotPasswordStep(ForgotPasswordStep.ForgotPassword)
    } else {
      setForgotPasswordStep(ForgotPasswordStep.ForgotUsername)
    }
  }

  const handleForgotUsernameSubmit = async () => {
    const { email } = formValue

    const response = await fetch('/auth/forgot-username', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    })

    if (response.status === 429) {
      setRateLimitError(true)
      return
    }

    setRateLimitError(false)

    setForgotPasswordStep(ForgotPasswordStep.Finished)
  }

  const handleForgotPasswordSubmit = async () => {
    const { username } = formValue

    const response = await fetch('/auth/reset-codes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username }),
    })

    if (response.status === 429) {
      setRateLimitError(true)
      return
    }

    setRateLimitError(false)

    setForgotPasswordStep(ForgotPasswordStep.Finished)
  }

  const handleBackButtonClick: React.MouseEventHandler<HTMLButtonElement> = (
    e,
  ) => {
    e.preventDefault()

    setForgotPasswordStep(ForgotPasswordStep.WhatsNeeded)
  }

  return (
    <Panel bordered>
      <Text weight="bold" size="xl" align="center" mb="1.5rem">
        Forgot Username or Password
      </Text>
      {forgotPasswordStep === ForgotPasswordStep.WhatsNeeded && (
        <>
          <Form onSubmit={handleRememberUsernameFormSubmit} fluid>
            <FormField
              name="remembersUsername"
              accepter={RadioGroup}
              label="Do you remember your username?"
            >
              <Radio value="yes">Yes</Radio>
              <Radio value="no">No</Radio>
            </FormField>
            <ButtonToolbar justify="center">
              <Button type="submit" appearance="primary">
                Next
              </Button>
            </ButtonToolbar>
          </Form>
        </>
      )}
      {forgotPasswordStep === ForgotPasswordStep.ForgotUsername && (
        <>
          <Text>
            Enter your email address and we will send you the usernames
            associated with your account. Once you've identified your username,
            you will be able to reset your password.
          </Text>
          {rateLimitError && (
            <Message
              type="error"
              closable
              onClose={() => setRateLimitError(false)}
            >
              <Text weight="bold">Error!</Text>
              <Text>{RATE_LIMIT_ERROR_MESSAGE}</Text>
            </Message>
          )}
          <Form
            className={styles.resetPasswordForm}
            onChange={(value) => setFormValue(value as FormValue)}
            onSubmit={() => {
              handleForgotUsernameSubmit().catch((error) => {
                console.error('Forgot username submit failed:', error)
              })
            }}
            fluid
          >
            <FormField name="email" label="Email Address" />
            <ButtonToolbar justify="center">
              <Button onClick={handleBackButtonClick}>Back</Button>
              <Button type="submit" appearance="primary">
                Submit
              </Button>
            </ButtonToolbar>
          </Form>
        </>
      )}
      {forgotPasswordStep === ForgotPasswordStep.ForgotPassword && (
        <>
          <p>Enter your username and we will send you a password reset link.</p>
          <p>The link in the email will expire after 10 minutes.</p>
          {rateLimitError && (
            <Message
              type="error"
              closable
              onClose={() => setRateLimitError(false)}
            >
              <Text weight="bold">Error!</Text>
              <Text>{RATE_LIMIT_ERROR_MESSAGE}</Text>
            </Message>
          )}
          <Form
            onSubmit={() => {
              handleForgotPasswordSubmit().catch((error) => {
                console.error('Forgot password submit failed:', error)
              })
            }}
            fluid
            onChange={(value) => setFormValue(value as FormValue)}
            formValue={formValue}
          >
            <FormField name="username" label="Username" />
            <ButtonToolbar justify="center">
              <Button onClick={handleBackButtonClick}>Back</Button>
              <Button type="submit" appearance="primary">
                Submit
              </Button>
            </ButtonToolbar>
          </Form>
        </>
      )}
      {forgotPasswordStep === ForgotPasswordStep.Finished && (
        <p>
          An email was sent with further instructions. Please check your inbox.
        </p>
      )}
      <div className={styles.returnToLogin}>
        <Link to="/login" viewTransition>
          Return to Login
        </Link>
      </div>
    </Panel>
  )
}

export default ForgotPassword
